import { PRODUCT_NO_IMAGE } from '../components/data'
import {
  IAccordionText,
  IBreadcrumb,
  IParamPick,
  IIGData,
  IProductDetail,
  IProductQuote,
  IProductSlide,
  IStories,
} from '../components/types'
import {
  product_instagram_nodes,
  product_product,
  product_product_sku_propertyValuesRelation_targetElement,
} from '../__generated__/product'
import { createCatalogElement } from './createCatalogItem'
import {
  getPropByCode,
  getPropDescriptionsByCode,
  getPropFileByCode,
  getPropFilesByCode,
  getPropValuesByCode,
} from './gql'
import { clearHtml, getImageSrc } from './utils'

export const createProduct = (element: product_product): IProductDetail => {
  if (!element) {
    return null
  }

  //price

  const oldPrice = element.priceBase
  const price =
    element.priceBaseWithDiscount === element.priceBase
      ? element.priceBase
      : element.priceBaseWithDiscount

  //recomended

  const recommendedElems = element.propertyValuesRelation?.filter(
    (item) => item.property.code === 'RECOMMEND',
  )

  const recomended =
    recommendedElems.length > 0
      ? recommendedElems[0]?.targetElements
          ?.filter((elem) => elem.active)
          ?.map((elem) => createCatalogElement(elem))
      : null

  //Accordeons

  const accordions: IAccordionText[] = []
  const values = getPropValuesByCode(element.propertyValues, 'DESCR')
  const descriptions = getPropDescriptionsByCode(
    element.propertyValues,
    'DESCR',
  )

  values?.map((value, index) => {
    accordions.push({
      title: descriptions[index],
      id: `product_accordeon_${index}`,
      description: value,
    })
  })

  //colors and aromates
  const colors: IParamPick[] = []
  const aromates: IParamPick[] = []

  element?.sku.map((skuItem) => {
    const info = skuItem?.propertyValuesRelation?.find(
      (p) => p.property.code === 'COLOR_REF',
    )?.targetHlElement
    const infoAromates = skuItem?.propertyValuesRelation?.find(
      (p) => p.property.code === 'AROMA',
    )?.targetHlElement
    const artNumber = getPropByCode(skuItem?.propertyValues, 'ARTNUMBER')

    if (info) {
      colors.push({
        id: skuItem.id,
        bx_id: skuItem.bx_id,
        image: info?.file?.url
          ? getImageSrc(info?.file?.url)
          : skuItem.detailPicture?.url
          ? getImageSrc(skuItem.detailPicture?.url)
          : PRODUCT_NO_IMAGE,
        item: info?.name || skuItem.name,
        artNumber,
        priceOld: +skuItem.priceBase,
        price: +skuItem.priceBaseWithDiscount,
      })
    } else if (infoAromates) {
      aromates.push({
        id: skuItem.id,
        bx_id: skuItem.bx_id,
        image: infoAromates?.file?.url
          ? getImageSrc(infoAromates?.file?.url)
          : skuItem.detailPicture?.url
          ? getImageSrc(skuItem.detailPicture?.url)
          : PRODUCT_NO_IMAGE,
        item: infoAromates?.name || skuItem.name,
        artNumber,
        priceOld: +skuItem.priceBase,
        price: +skuItem.priceBaseWithDiscount,
      })
    }
  })

  //subsets
  const subsets = []
  const subsetsElements = element?.propertyValuesRelation?.find?.(
    (p) => p.property.code === 'SET',
  )?.targetElements

  if (subsetsElements?.length > 0) {
    //transform subsetsElements to array for sorting
    const subsArr = []
    subsetsElements.map((el) => subsArr.push(el))

    subsets.push(
      ...subsArr?.sort((a, b) => a.sort - b.sort)?.map(createProduct),
    )
  }

  //quote
  const hasQuote = !!(
    getPropFileByCode(element.propertyValuesRelation, 'QUOTE_IMAGE') &&
    getPropByCode(element.propertyValues, 'QUOTE')
  )

  const quote: IProductQuote = hasQuote
    ? {
        img: getPropFileByCode(element.propertyValuesRelation, 'QUOTE_IMAGE'),
        quote: getPropByCode(element.propertyValues, 'QUOTE'),
        source: getPropByCode(element.propertyValues, 'QUOTE_SRC'),
        author: {
          img: getPropFileByCode(
            element.propertyValuesRelation,
            'QUOTE_AUTHOR_IMG',
          ),
          description: getPropByCode(
            element.propertyValues,
            'QUOTE_AUTHOR_INFO',
          ),
        },
      }
    : null

  const extDescriptions = getPropDescriptionsByCode(
    element?.propertyValues,
    'EXT_PARAMS',
  )
  const extValues = getPropValuesByCode(element?.propertyValues, 'EXT_PARAMS')

  const external = extDescriptions?.map((descr, index) => ({
    description: descr,
    value: extValues[index],
  }))

  return {
    id: element.id,
    title: element.name,
    img: element.detailPicture?.url
      ? getImageSrc(element.detailPicture.url)
      : getImageSrc(element.previewPicture?.url),
    price,
    bx_id: element.bx_id,
    oldPrice,
    description: clearHtml(element.previewText),
    rate: +getPropByCode(element.propertyValues, 'RATE') || null,
    reviewCount: +getPropByCode(element.propertyValues, 'RATE_COUNT') || 0,
    recomended,
    accordions,
    subtitle: getPropByCode(element.propertyValuesEnum, 'CAPTION'),
    colors,
    aromates,
    subsets,
    quote,
    external,
  }
}

export const createProductSlider = (
  element: product_product,
  skuCode?: string,
): IProductSlide[] => {
  const slides: IProductSlide[] = []

  if (!element) {
    return null
  }

  if (skuCode) {
    const currentSku = element?.sku?.filter((item) => item.id === skuCode)[0]

    const itemPics = getPropFilesByCode(
      currentSku?.propertyValuesRelation,
      'MORE_PHOTO',
    )

    itemPics?.map((image) =>
      slides.push({
        img: getImageSrc(image.url),
        id: image.id,
        name: `${currentSku.name}`,
      }),
    )

    if (slides.length === 0 && currentSku?.detailPicture?.url) {
      slides.push({
        img: getImageSrc(currentSku.detailPicture.url),
        id: currentSku.detailPicture.id,
        name: currentSku.name,
      })
    }
  } else {
    const hover = getPropFileByCode(
      element?.propertyValuesRelation,
      'HOVER_IMAGE',
    )
    const gallery = getPropFilesByCode(
      element.propertyValuesRelation,
      'MORE_PHOTO',
    )

    if (gallery?.length > 0) {
      gallery.map((image) =>
        slides.push({
          img: getImageSrc(image.url),
          name: element.name,
          id: image.id,
        }),
      )
    } else if (element.detailPicture?.url || hover) {
      if (element.detailPicture?.url) {
        slides.push({
          img: getImageSrc(element.detailPicture.url),
          id: element.detailPicture.id,
          name: element.name,
        })
      }
      if (hover) {
        slides.push({
          img: getImageSrc(hover),
          id: `${element.detailPicture?.id}_hover`,
          name: element.name,
        })
      }
    }

    element?.sku?.map((item) => {
      const itemPics = getPropFilesByCode(
        item.propertyValuesRelation,
        'MORE_PHOTO',
      )

      itemPics?.map((image) =>
        slides.push({
          img: getImageSrc(image.url),
          id: image.id,
          name: item.name,
        }),
      )
    })
  }

  return slides
}

export const getSelectedSku = (
  element: product_product,
  skuCode: string,
): product_product_sku_propertyValuesRelation_targetElement => {
  const selectedSku = element?.sku?.filter((item) => item.id === skuCode)[0]

  const el = selectedSku?.propertyValuesRelation?.filter(
    (val) => val?.property?.code === 'PROBE',
  )[0]?.targetElement

  return el
}

export const createBreadcrumbs = (element: product_product): IBreadcrumb[] => {
  if (!element || !element.sections) {
    null
  }

  const crumbs = []

  if (element?.parentSection?.code) {
    if (
      element?.parentSection?.level === 1 &&
      element?.parentSection?.childSections?.length !== 0
    ) {
      const childSection = element?.sections?.filter(
        (sec) =>
          element?.parentSection?.childSections?.findIndex(
            (childSec) => childSec.id === sec.id,
          ) >= 0,
      )?.[0]

      if (childSection) {
        crumbs.push(
          {
            link: `/catalog/${element?.parentSection?.code}/${childSection.code}`,
            name: childSection.name,
            id: childSection.id,
          },
          {
            link: `/catalog/${element?.parentSection?.code}`,
            name: element?.parentSection?.name,
            id: element?.parentSection?.id,
          },
        )
      }
    } else if (
      element?.parentSection?.level > 1 &&
      element?.parentSection?.parentSection
    ) {
      crumbs.push(
        {
          link: `/catalog/${element?.parentSection?.parentSection.code}/${element?.parentSection?.code}`,
          name: element?.parentSection?.name,
          id: element?.parentSection?.id,
        },
        {
          link: `/catalog/${element?.parentSection?.parentSection?.code}`,
          name: element?.parentSection?.parentSection?.name,
          id: element?.parentSection?.parentSection?.id,
        },
      )
    }
  } else {
    const filteredSections = element?.sections.filter(
      (section) => section.code !== 'sale',
    )

    if (filteredSections?.length > 0) {
      crumbs.push(
        {
          link: `/catalog/${filteredSections[0]?.parentSection?.code}/${filteredSections[0]?.code}`,
          name: filteredSections[0]?.name,
          id: filteredSections[0].id,
        },
        {
          link: `/catalog/${filteredSections[0]?.parentSection?.code}`,
          name: filteredSections[0]?.parentSection?.name,
          id: filteredSections[0]?.parentSection?.id,
        },
      )
    }
  }

  return crumbs.reverse()
}

export const createStories = (element: product_product): IStories[] => {
  const storiesElems = element?.propertyValuesRelation
    ?.filter((val) => val.property.code === 'STORIES')
    ?.map((val) => val.targetElements)[0]
    ?.filter((val) => !!val.active)

  const stories: IStories[] = storiesElems?.map((story) => {
    return {
      id: story.id,
      title: story.name,
      img: story.previewPicture?.url,
      detailImg: getPropFilesByCode(
        story.propertyValuesRelation,
        'IMAGES',
      )?.map((file) => ({
        url: file?.url,
        id: file?.id,
      })),
    }
  })

  return stories || []
}

export const createProductInstagram = (
  elements: product_instagram_nodes[],
): IIGData[] => {
  if (elements?.length < 1) {
    return null
  }

  const instagram: IIGData[] = elements?.map((elem) => ({
    code: elem.id,
    photo: elem.detailPicture?.url,
    link: getPropByCode(elem.propertyValues, 'LINK'),
  }))

  return instagram
}
